import { useEffect, useRef, useState } from 'react';
import { Moon, Sun, Monitor } from 'lucide-react';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';

/**
 * Single-tap, no-dropdown theme switch cycling Light → Dark → System.
 * The currently active mode is shown as the icon; clicking advances to the
 * next option. System mode renders a monitor glyph and follows the OS
 * preference via `next-themes`.
 */
const ORDER = ['light', 'dark', 'system'] as const;
type Mode = (typeof ORDER)[number];

const LABELS: Record<Mode, string> = {
  light: 'Light Mode',
  dark: 'Dark Mode',
  system: 'System',
};

// Dev-only logger. Tree-shaken in production builds because
// `import.meta.env.DEV` is statically replaced by Vite.
// Toggle verbose output at runtime via `window.__themeDebug = true`
// (or set localStorage key `theme:debug` to `"1"`).
const isThemeDebugEnabled = (): boolean => {
  if (!import.meta.env.DEV) return false;
  if (typeof window === 'undefined') return false;
  try {
    if ((window as unknown as { __themeDebug?: boolean }).__themeDebug) return true;
  } catch {
    // Accessing window properties can throw in sandboxed iframes.
  }
  try {
    // `localStorage` access throws in some privacy modes / sandboxed
    // contexts before we ever get to call `.getItem`.
    return window.localStorage?.getItem('theme:debug') === '1';
  } catch {
    return false;
  }
};
const themeLog = (event: string, data: Record<string, unknown>) => {
  try {
    if (!isThemeDebugEnabled()) return;
    // Some environments (older browsers, certain test runners,
    // stripped consoles) lack `console.debug`. Fall back to `log`,
    // and finally to a no-op — never let logging crash the UI.
    const sink: ((...args: unknown[]) => void) | undefined =
      typeof console !== 'undefined'
        ? (typeof console.debug === 'function'
            ? console.debug.bind(console)
            : typeof console.log === 'function'
              ? console.log.bind(console)
              : undefined)
        : undefined;
    if (!sink) return;
    sink(
      `%c[theme]%c ${event}`,
      'color:#147BA6;font-weight:600',
      'color:inherit',
      { t: new Date().toISOString().slice(11, 23), ...data },
    );
  } catch {
    // Logging must never break rendering.
  }
};

const ThemeToggle = () => {
  const { theme, setTheme, resolvedTheme, systemTheme } = useTheme();
  // Avoid icon flicker before next-themes has read the persisted value
  // from localStorage on first paint.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Track the OS preference only while the user is in "System" mode.
  // Outside of System mode the OS value is irrelevant for the rendered
  // glyph, so we skip the subscription entirely to avoid re-renders.
  // We also coalesce updates: only flip state when the resolved value
  // actually changes (matchMedia can fire spuriously in some browsers).
  const isSystem = mounted && theme === 'system';
  const [osIsDark, setOsIsDark] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  useEffect(() => {
    if (!isSystem) return;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    // Sync once on (re)subscribe in case the OS changed while we were
    // not listening (e.g. user just switched back to System mode).
    setOsIsDark((prev) => (prev === mql.matches ? prev : mql.matches));
    themeLog('subscribe', { osIsDark: mql.matches });

    const onChange = (e: MediaQueryListEvent | MediaQueryList) => {
      // Enable the smooth transition class around the OS-driven theme
      // swap so System-mode users get the same fade as manual toggles
      // instead of a hard flash when the OS flips light/dark.
      if (typeof document !== 'undefined') {
        const root = document.documentElement;
        root.classList.add('theme-transition');
        window.setTimeout(() => root.classList.remove('theme-transition'), 250);
      }
      themeLog('os-change', { matches: e.matches });
      setOsIsDark((prev) => (prev === e.matches ? prev : e.matches));
    };

    // Prefer the modern API; fall back to the deprecated addListener
    // for Safari < 14 and other legacy engines. Track which path we
    // used so cleanup mirrors it exactly — this prevents duplicate
    // listeners across StrictMode double-invokes or fast re-subscribes.
    let detach: () => void;
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange as (e: MediaQueryListEvent) => void);
      detach = () =>
        mql.removeEventListener('change', onChange as (e: MediaQueryListEvent) => void);
    } else if (typeof (mql as MediaQueryList & {
      addListener?: (l: (e: MediaQueryListEvent) => void) => void;
      removeListener?: (l: (e: MediaQueryListEvent) => void) => void;
    }).addListener === 'function') {
      const legacy = mql as MediaQueryList & {
        addListener: (l: (e: MediaQueryListEvent) => void) => void;
        removeListener: (l: (e: MediaQueryListEvent) => void) => void;
      };
      legacy.addListener(onChange as (e: MediaQueryListEvent) => void);
      detach = () => legacy.removeListener(onChange as (e: MediaQueryListEvent) => void);
    } else {
      detach = () => {};
    }

    let detached = false;
    return () => {
      if (detached) return;
      detached = true;
      detach();
      themeLog('unsubscribe', {});
    };
  }, [isSystem]);

  const current: Mode =
    mounted && (ORDER as readonly string[]).includes(theme ?? '')
      ? (theme as Mode)
      : 'system';
  const next: Mode = ORDER[(ORDER.indexOf(current) + 1) % ORDER.length];

  // Single source of truth for "is the effective UI currently dark?".
  // Priority chain (most authoritative first):
  //   1. Explicit user choice (`light` / `dark`) — wins unconditionally,
  //      so a stale resolvedTheme can never produce a contradicting icon
  //      while next-themes is still settling after `setTheme(...)`.
  //   2. `resolvedTheme` from next-themes — already accounts for System
  //      and is the authoritative value once hydrated.
  //   3. `systemTheme` — fallback before resolvedTheme is ready.
  //   4. Locally tracked `osIsDark` — last-resort fallback when
  //      next-themes has not reported anything yet (very first paint).
  const effectiveIsDark: boolean = (() => {
    if (current === 'light') return false;
    if (current === 'dark') return true;
    const fromLib = resolvedTheme ?? systemTheme;
    if (fromLib === 'dark') return true;
    if (fromLib === 'light') return false;
    return osIsDark;
  })();

  // The icon is derived purely from `effectiveIsDark`, so the rendered
  // glyph can never disagree with the active theme — regardless of which
  // signal (user choice, next-themes, or matchMedia) settled it.
  const Icon = effectiveIsDark ? Moon : Sun;

  // Render-trace: log every render with the inputs that produced the
  // current icon, plus a render counter to make duplicate renders
  // easy to spot. Runs only in dev when debug flag is on.
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  // Track previous values to compute a diff of which inputs actually
  // changed between renders. This makes it trivial to spot whether a
  // re-render was driven by a user switch (`theme`), next-themes
  // settling (`resolvedTheme` / `systemTheme`), or the OS flipping
  // (`osIsDark`) — versus a render caused by an unrelated parent.
  const prevInputsRef = useRef<{
    theme: string | undefined;
    resolvedTheme: string | undefined;
    systemTheme: string | undefined;
    osIsDark: boolean;
  } | null>(null);
  if (mounted) {
    const nextInputs = {
      theme,
      resolvedTheme,
      systemTheme,
      osIsDark,
    };
    const prev = prevInputsRef.current;
    const changed: Record<string, { from: unknown; to: unknown }> = {};
    if (prev) {
      (Object.keys(nextInputs) as Array<keyof typeof nextInputs>).forEach((k) => {
        if (prev[k] !== nextInputs[k]) {
          changed[k] = { from: prev[k], to: nextInputs[k] };
        }
      });
    }
    const triggers = prev ? Object.keys(changed) : ['initial'];
    themeLog('render', {
      n: renderCountRef.current,
      theme,
      resolvedTheme,
      systemTheme,
      osIsDark,
      current,
      effectiveIsDark,
      icon: effectiveIsDark ? 'Moon' : 'Sun',
      triggers,
      changed,
    });
    prevInputsRef.current = nextInputs;
  }

  // Briefly enable a global transition class so theme switches animate
  // smoothly. Initial loads never get this class → no animation flash.
  const handleSwitch = () => {
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      root.classList.add('theme-transition');
      window.setTimeout(() => root.classList.remove('theme-transition'), 250);
    }
    themeLog('user-switch', { from: current, to: next });
    setTheme(next);
  };

  // Render a stable placeholder until mounted to avoid SSR/CSR hydration
  // mismatches (next-themes only knows the persisted value client-side).
  if (!mounted) {
    return (
      <Button
        variant="ghost"
        size="icon"
        aria-hidden="true"
        tabIndex={-1}
        className="relative w-9 h-9 rounded-xl text-muted-foreground"
      >
        <Monitor className="h-4 w-4 opacity-0" strokeWidth={1.75} />
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleSwitch}
      aria-label={`Theme: ${LABELS[current]} – wechseln zu ${LABELS[next]}`}
      title={`${LABELS[current]} → ${LABELS[next]}`}
      className="relative w-9 h-9 rounded-xl text-muted-foreground hover:text-foreground hover:bg-primary/[0.06] transition-colors duration-200"
    >
      <Icon className="h-4 w-4 transition-all duration-200" strokeWidth={1.75} />
      {current === 'system' && (
        <span
          aria-hidden="true"
          className="absolute bottom-1 right-1 h-1.5 w-1.5 rounded-full bg-primary"
        />
      )}
    </Button>
  );
};

export default ThemeToggle;
